'use client'

import * as stylex from '@stylexjs/stylex'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { breakpoints, colors, fontSizes, motion, radii, spacing } from '../tokens/tokens.stylex'

export const loadingDelayMs = 2000
const RevealedTogether = createContext(false)

type NavigationRead = {
  identity: string
  pending: boolean
  started: number
}

type NavigationDeadline = {
  identity: string
  started: number
}

const NavigationReadContext = createContext<NavigationRead | undefined>(undefined)

export type NavigationPresentationProps = {
  identity: string
  pending: boolean
  startedAt?: number | undefined
  children: ReactNode
}

/**
 * Share one loading deadline across route code and page data. Applications supply navigation identity,
 * readiness, and an optional initial document timestamp. Use ordinary AsyncRegion for independent reads.
 * Status: supported.
 */
export function NavigationPresentation({ identity, pending, startedAt, children }: NavigationPresentationProps) {
  const [deadline, setDeadline] = useState<NavigationDeadline>(() => ({ identity, started: startedAt ?? Date.now() }))

  if (deadline.identity !== identity) {
    // oxlint-disable-next-line react/purity -- Each navigation identity owns a fresh browser-clock deadline.
    setDeadline({ identity, started: Date.now() })
  }

  const navigation = useMemo(
    () => ({ identity, pending, started: deadline.started }),
    [identity, pending, deadline.started]
  )

  return <NavigationReadContext value={navigation}>{children}</NavigationReadContext>
}

const styles = stylex.create({
  region: { position: 'relative', minWidth: 0 },
  fill: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
  placeholder: {
    pointerEvents: 'none',
    opacity: 0,
    transitionProperty: 'opacity',
    transitionDuration: { default: motion.loadingRevealDuration, [breakpoints.reducedMotion]: '0s' },
    transitionTimingFunction: 'ease-out',
  },
  visible: { opacity: 1 },
  exit: {
    position: 'absolute',
    insetBlockStart: 0,
    insetInlineStart: 0,
    width: '100%',
    animationName: stylex.keyframes({ from: { opacity: 1 }, to: { opacity: 0 } }),
    animationDuration: { default: motion.loadingRevealDuration, [breakpoints.reducedMotion]: '0s' },
    animationFillMode: 'forwards',
    animationTimingFunction: 'ease-out',
  },
  reveal: {
    animationName: stylex.keyframes({ from: { opacity: 0 }, to: { opacity: 1 } }),
    animationDuration: { default: motion.loadingRevealDuration, [breakpoints.reducedMotion]: '0s' },
    animationTimingFunction: 'ease-out',
  },
  announcement: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap',
  },
  feedback: {
    position: 'absolute',
    insetBlockStart: 0,
    insetInlineEnd: 0,
    pointerEvents: 'none',
    color: colors.muted,
    fontSize: fontSizes.caption,
    paddingBlock: spacing.space4,
  },
  block: (width: number | string, height: number | string) => ({
    display: 'block',
    flexShrink: 0,
    width,
    height,
    maxWidth: '100%',
    borderRadius: radii.small,
    backgroundColor: colors.surfaceAlt,
  }),
  text: (width: number | string) => ({
    display: 'inline-block',
    width,
    maxWidth: '100%',
    verticalAlign: 'top',
  }),
  line: { display: 'grid', alignItems: 'center', height: '1lh' },
})

/**
 * Add delayed, noninteractive route progress without displacing content. Place it in a positioned route
 * frame under NavigationPresentation. Applications retain the committed route while replacement code loads.
 * Status: supported.
 */
export function NavigationFeedback() {
  const navigation = useContext(NavigationReadContext)

  if (!navigation?.pending) return null

  return (
    <div {...stylex.props(styles.feedback)}>
      <AsyncRegion page pending label="Opening page" placeholder={<span>Opening page…</span>} />
    </div>
  )
}

export type AsyncRegionProps = {
  pending: boolean
  label: string
  placeholder: ReactNode
  children?: ReactNode
  page?: boolean
  fill?: boolean
}

type RegionPresentation = {
  navigationIdentity: string | undefined
  pending: boolean
  cycle: number
  enteredFromPending: boolean
  placeholderVisible: boolean
  exiting: boolean
  presentedNavigation: number | undefined
}

/**
 * Reserve missing content's geometry immediately and delay decorative loading feedback. Applications
 * supply a content-shaped placeholder and keep pending false when usable, authorized results already exist.
 * Use page under NavigationPresentation to share a route deadline; use Alert for immediate failures.
 * Placeholder content must be decorative, without forms, editors, requests, or other side effects.
 * Status: supported.
 */
export function AsyncRegion({ pending, label, placeholder, children, page = false, fill = false }: AsyncRegionProps) {
  const navigation = useContext(NavigationReadContext)

  const [presentation, setPresentation] = useState<RegionPresentation>(() => ({
    navigationIdentity: page ? navigation?.identity : undefined,
    pending,
    cycle: 0,
    enteredFromPending: false,
    placeholderVisible: false,
    exiting: false,
    presentedNavigation: pending ? undefined : navigation?.started,
  }))

  const navigationIdentity = page ? navigation?.identity : undefined
  const navigationChanged = presentation.navigationIdentity !== navigationIdentity

  if (presentation.pending !== pending || navigationChanged) {
    setPresentation({
      navigationIdentity,
      pending,
      cycle: presentation.cycle + (pending ? 1 : 0),
      enteredFromPending: !pending && !navigationChanged,
      placeholderVisible: false,
      exiting: !pending && !navigationChanged && presentation.placeholderVisible,
      presentedNavigation: pending ? presentation.presentedNavigation : navigation?.started,
    })
  } else if (!pending && presentation.presentedNavigation !== navigation?.started) {
    setPresentation({ ...presentation, presentedNavigation: navigation?.started })
  }

  const cycle = presentation.cycle
  const sharedStart = page && navigation?.started !== presentation.presentedNavigation ? navigation?.started : undefined

  const onVisible = useCallback(() => {
    setPresentation((current) =>
      current.cycle === cycle && current.pending ? { ...current, placeholderVisible: true } : current
    )
  }, [cycle])

  const onExited = useCallback(() => {
    setPresentation((current) => (current.cycle === cycle ? { ...current, exiting: false } : current))
  }, [cycle])

  return (
    <div aria-busy={pending} {...stylex.props(styles.region, fill && styles.fill)}>
      {(pending || presentation.exiting) && (
        <PendingPlaceholder
          key={String(page ? navigation?.started : 'region') + ':' + cycle}
          pending={pending}
          label={label}
          startedAt={sharedStart}
          fill={fill}
          onVisible={onVisible}
          onExited={onExited}
        >
          {placeholder}
        </PendingPlaceholder>
      )}
      {!pending && (
        <ContentReveal identity={navigationIdentity} enter={presentation.enteredFromPending} fill={fill}>
          {children}
        </ContentReveal>
      )}
    </div>
  )
}

type PendingPlaceholderProps = {
  pending: boolean
  label: string
  children: ReactNode
  startedAt: number | undefined
  fill: boolean
  onVisible(): void
  onExited(): void
}

function PendingPlaceholder({
  pending,
  label,
  children,
  startedAt,
  fill,
  onVisible,
  onExited,
}: PendingPlaceholderProps) {
  const [started] = useState(() => startedAt ?? Date.now())
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!pending) return

    const remaining = Math.max(0, loadingDelayMs - (Date.now() - started))

    const timer = setTimeout(() => {
      setVisible(true)
      onVisible()
    }, remaining)

    return () => clearTimeout(timer)
  }, [pending, started, onVisible])

  return (
    <div
      onAnimationEnd={(event) => {
        if (!pending && event.currentTarget === event.target) onExited()
      }}
      {...stylex.props(styles.placeholder, visible && styles.visible, !pending && styles.exit, fill && styles.fill)}
    >
      {visible && pending && (
        <output aria-label={label} {...stylex.props(styles.announcement)}>
          {label}
        </output>
      )}
      <div aria-hidden="true" inert {...stylex.props(fill && styles.fill)}>
        {children}
      </div>
    </div>
  )
}

type ContentRevealProps = {
  identity: string | undefined
  children: ReactNode
  enter: boolean
  fill: boolean
}

type ContentPresentation = {
  identity: string | undefined
  animate: boolean
}

function ContentReveal({ identity, children, enter, fill }: ContentRevealProps) {
  const together = useContext(RevealedTogether)

  const [presentation, setPresentation] = useState<ContentPresentation>(() => ({
    identity,
    animate: enter && !together,
  }))

  if (presentation.identity !== identity) {
    setPresentation({ identity, animate: false })
  }

  const animate = presentation.animate && presentation.identity === identity

  return (
    <div
      onAnimationEnd={(event) => {
        if (event.currentTarget === event.target) {
          setPresentation((current) => (current.animate ? { ...current, animate: false } : current))
        }
      }}
      {...stylex.props(animate && styles.reveal, fill && styles.fill)}
    >
      <RevealedTogether value={animate || together}>{children}</RevealedTogether>
    </div>
  )
}

export type SkeletonProps = {
  width?: number | string
  height?: number | string
}

/**
 * Reserve decorative block geometry inside an AsyncRegion placeholder. Applications supply structural
 * dimensions that match their content. Use SkeletonText when the inherited text line box determines height.
 * Status: supported.
 */
export function Skeleton({ width = '100%', height = 12 }: SkeletonProps) {
  return <span aria-hidden="true" {...stylex.props(styles.block(width, height))} />
}

export type SkeletonTextProps = {
  width?: number | string
  lines?: number
}

/**
 * Reserve inherited text line boxes with smaller decorative bars. Applications supply approximate text
 * width and line count. Use Skeleton for controls, media, or other non-text geometry.
 * Status: supported.
 */
export function SkeletonText({ width = '100%', lines = 1 }: SkeletonTextProps) {
  return (
    <span aria-hidden="true" {...stylex.props(styles.text(width))}>
      {Array.from({ length: lines }, (_, index) => (
        <span key={index} {...stylex.props(styles.line)}>
          <Skeleton height="0.65em" />
        </span>
      ))}
    </span>
  )
}
